// Line Entry — the source editor (a modal with tabs).
//
// A SOURCE is data: where a line form's candidate rows come from (another
// transaction's form, a master table / a column's lookup, a business-logic
// function, a General Process), how they map onto line columns, which columns
// they link / lock / pre-fill, and the limits the engine checks again at
// save. The buttons the user sees are COMMANDS over a source
// (LineEntryPanel). Model: docs/plans/line-entry-development-plan.md §2.

import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Wand2, Play, X, Loader2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import type {
  ColumnDef, LineLimit, LineSource, LineSourceKind, SqlModel, TransactionModel,
} from './types'
import { normalizeItemChange } from './types'
import { allSqlModels } from '@twasta/page-runtime/txn/utils'
import BusinessLogicFields from './BusinessLogicFields'
import LineProcessParams from './LineProcessParams'
import {
  livePreviewExecute, livePreviewLookupSource, livePreviewTransactions,
} from '@/livepreview/api'
import { parseApiError } from '@/livepreview/utils'

interface Props {
  open: boolean
  source: LineSource
  /** Names already used by OTHER sources on this form. */
  takenNames: string[]
  lineSm: SqlModel
  parentSm: SqlModel | null
  model: TransactionModel
  project: string
  formNo: string
  /** Commands that use this source (the Test tab runs one of them). */
  commandNames: string[]
  /** Open on this tab (the button panel's "Test with a sample record"). */
  initialTab?: Tab
  initialCommand?: string
  onClose: () => void
  onSave: (next: LineSource) => void
}

export type Tab = 'source' | 'documents' | 'map' | 'limits' | 'picker' | 'test'

const KIND_LABELS: Record<LineSourceKind, { label: string; hint: string }> = {
  transaction: { label: 'Another transaction', hint: 'Lines of another document (PO lines → receipt lines). Its row security and rights apply.' },
  master: { label: 'Master table', hint: 'Rows of a master (items, services) — usually a column\'s own lookup. "Pick many".' },
  business_logic: { label: 'Business logic', hint: 'A function returns the rows (keyed by THIS form\'s columns) with per-row limits. It is called again at save to verify.' },
  process: { label: 'General Process', hint: 'A General Process computes the rows (pending across receipts, conversions, allocation).' },
}

const label: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '10px 0 3px',
}
const input: React.CSSProperties = {
  width: '100%', padding: '5px 8px', background: 'var(--color-input-bg)', color: 'var(--color-text)',
  border: '1px solid var(--color-border-strong)', borderRadius: 4, fontSize: '0.8625rem',
}
const help: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 3 }
const smallBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: '0.8125rem',
  border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)',
  color: 'var(--color-text)', cursor: 'pointer',
}

function colsOf(sm: SqlModel | null | undefined): ColumnDef[] {
  return (sm?.columns || []).map(w => w?.column).filter(Boolean) as ColumnDef[]
}

function clean<T extends object>(o: T): T {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
    if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue
    if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) continue
    out[k] = v
  }
  return out as T
}

/** Chips for an ordered list of column names, with an "add" picker. */
function ColumnChips({ value, options, onChange, placeholder }: {
  value: string[]; options: string[]; onChange: (v: string[]) => void; placeholder: string
}) {
  const rest = options.filter(o => !value.includes(o))
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {value.map(v => (
        <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px',
          borderRadius: 10, fontSize: '0.8125rem', background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)' }}>
          {v}
          <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(value.filter(x => x !== v))}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
            <X size={11} />
          </button>
        </span>
      ))}
      {rest.length > 0 && (
        <select value="" onChange={e => e.target.value && onChange([...value, e.target.value])}
          style={{ ...input, width: 'auto', padding: '2px 6px', fontSize: '0.8125rem' }} aria-label={placeholder}>
          <option value="">{placeholder}</option>
          {rest.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
    </div>
  )
}

export default function LineSourceEditor({
  open, source, takenNames, lineSm, parentSm, model, project, formNo, commandNames, onClose, onSave,
  initialTab, initialCommand,
}: Props) {
  const [tab, setTab] = useState<Tab>(initialTab || 'source')
  const [draft, setDraft] = useState<LineSource>(source)
  const [txns, setTxns] = useState<Array<{ obj_name: string; title: string }>>([])
  const [srcModel, setSrcModel] = useState<TransactionModel | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) { setDraft(source); setTab(initialTab || 'source'); setError(null) }
  }, [open, source, initialTab])

  const kind = draft.from?.kind || 'transaction'
  const lineCols = useMemo(() => colsOf(lineSm), [lineSm])
  const lineNames = lineCols.map(c => c.name)
  const parentNames = colsOf(parentSm).map(c => c.name)

  // The transaction list + the chosen source object's model (its forms and
  // columns drive the form picker, auto-map, source_key and link.from).
  useEffect(() => {
    if (!open || kind !== 'transaction') return
    livePreviewTransactions(project)
      .then(r => setTxns(r.transactions || []))
      .catch(err => setError(`Could not list transactions: ${parseApiError(err).message}`))
  }, [open, kind, project])
  const srcObj = draft.from?.object || ''
  useEffect(() => {
    if (!open || kind !== 'transaction' || !srcObj) { setSrcModel(null); return }
    livePreviewLookupSource(project, { objName: srcObj })
      .then(r => setSrcModel(r.transaction as TransactionModel))
      .catch(err => { setSrcModel(null); setError(`Could not load '${srcObj}': ${parseApiError(err).message}`) })
  }, [open, kind, srcObj, project])
  const srcForms = useMemo(() => (srcModel ? allSqlModels(srcModel) : []), [srcModel])
  const srcFormSm = srcForms.find(f => f.formNo === String(draft.from?.form_no || '1'))?.sm
    || srcForms[0]?.sm || null
  const srcNames = colsOf(srcFormSm).map(c => c.name)

  const patch = (p: Partial<LineSource>) => setDraft(d => ({ ...d, ...p }))
  const patchFrom = (p: Partial<LineSource['from']>) => setDraft(d => ({ ...d, from: { ...d.from, ...p } }))

  const nameClash = takenNames.includes(draft.name)
  const save = () => {
    if (!draft.name.trim()) { setError('A source needs a name.'); return }
    if (nameClash) { setError(`Another source is already named '${draft.name}'.`); return }
    const next = clean({ ...draft }) as LineSource
    next.from = clean({ ...draft.from }) as LineSource['from']
    if (next.picker) next.picker = clean({ ...next.picker })
    if (next.documents) {
      next.documents = clean({ ...next.documents })
      if (next.documents.choose) next.documents.choose = clean({ ...next.documents.choose })
    }
    onSave(next)
  }

  const tabs: Array<[Tab, string]> = [
    ['source', 'Source'], ['documents', 'Documents'], ['map', 'Map'],
    ['limits', 'Fill & limits'], ['picker', 'Picker'], ['test', 'Test'],
  ]

  return (
    <Modal open={open} onClose={onClose} title={`Line source — ${draft.label || draft.name || 'new'}`}
      size="full" resizable maximizable storageKey="line-source-editor"
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          {error && <span style={{ flex: 1, color: '#ef4444', fontSize: '0.8125rem' }}>{error}</span>}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={save}>Save source</button>
        </div>
      )}>
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--color-border)', marginBottom: 8 }}>
        {tabs.map(([t, l]) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            style={{ padding: '6px 12px', fontSize: '0.8425rem', border: 'none', cursor: 'pointer',
              background: 'transparent', color: tab === t ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              borderBottom: tab === t ? '2px solid var(--color-accent)' : '2px solid transparent' }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'source' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={label}>Name (unique on this form)</label>
              <input style={{ ...input, borderColor: nameClash ? '#ef4444' : undefined }} value={draft.name}
                onChange={e => patch({ name: e.target.value.replace(/\s+/g, '_') })} />
            </div>
            <div>
              <label style={label}>Label</label>
              <input style={input} value={draft.label || ''} onChange={e => patch({ label: e.target.value })} />
            </div>
          </div>
          <label style={label}>Where the lines come from</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6 }}>
            {(Object.keys(KIND_LABELS) as LineSourceKind[]).map(k => (
              <label key={k} style={{ display: 'block', padding: 8, borderRadius: 6, cursor: 'pointer',
                border: `1px solid ${kind === k ? 'var(--color-accent)' : 'var(--color-border)'}` }}>
                <input type="radio" checked={kind === k} onChange={() => patchFrom({ kind: k })} />
                {' '}<b style={{ fontSize: '0.8625rem' }}>{KIND_LABELS[k].label}</b>
                <div style={help}>{KIND_LABELS[k].hint}</div>
              </label>
            ))}
          </div>

          {kind === 'transaction' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div>
                <label style={label}>Transaction</label>
                <select style={input} value={draft.from.object || ''} onChange={e => patchFrom({ object: e.target.value })}>
                  <option value="">— choose —</option>
                  {txns.map(t => <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name} ({t.obj_name})</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Its form</label>
                <select style={input} value={String(draft.from.form_no || '')} onChange={e => patchFrom({ form_no: e.target.value })}>
                  {srcForms.map(f => <option key={f.formNo} value={f.formNo}>{f.formNo} — {f.sm.form_title}</option>)}
                </select>
              </div>
            </div>
          )}
          {kind === 'master' && (
            <div>
              <label style={label}>Use the lookup of a column</label>
              <select style={input} value={draft.from.lookup_of || ''}
                onChange={e => patchFrom({ lookup_of: e.target.value || undefined, table: e.target.value ? undefined : draft.from.table })}>
                <option value="">— a table instead —</option>
                {lineCols.filter(c => (c.lookup || '').trim()).map(c => (
                  <option key={c.name} value={c.name}>{c.name} — {c.lookup}</option>
                ))}
              </select>
              {!draft.from.lookup_of && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={label}>Table</label>
                    <input style={input} value={draft.from.table || ''} onChange={e => patchFrom({ table: e.target.value })} />
                  </div>
                  <div>
                    <label style={label}>Key column (identifies a row)</label>
                    <input style={input} value={draft.from.key || ''} onChange={e => patchFrom({ key: e.target.value })} />
                  </div>
                </div>
              )}
              <label style={label}>Filter (SQL condition; {'{header.X}'} binds the header)</label>
              <input style={input} value={draft.from.filter || ''} onChange={e => patchFrom({ filter: e.target.value })}
                placeholder="ACTIVE = 'Y'" />
            </div>
          )}
          {kind === 'business_logic' && (
            <BusinessLogicFields
              value={{ method: draft.from.business_logic?.method || '', language: (draft.from.business_logic?.language as 'plpgsql') || undefined }}
              onChange={bl => patchFrom({ business_logic: { method: bl.method, ...(bl.language ? { language: bl.language } : {}) } })}
              methodLabel="Function returning the rows"
            />
          )}
          {kind === 'process' && (
            <LineProcessParams project={project} from={draft.from} parentNames={parentNames}
              onChange={patchFrom} />
          )}
        </div>
      )}

      {tab === 'documents' && (
        <div>
          <div style={help}>
            A document is the record the lines come from (the PO for a receipt). "This record's" uses the header column;
            "choose" opens a chooser over the lookup below (another PO of the same supplier, several at once).
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={label}>Header column naming the document</label>
              <select style={input} value={draft.documents?.header_column || ''}
                onChange={e => patch({ documents: { ...(draft.documents || {}), header_column: e.target.value || undefined } })}>
                <option value="">— none —</option>
                {parentNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {kind === 'transaction' && (
              <div>
                <label style={label}>Source column the document id matches</label>
                <select style={input} value={draft.documents?.source_key || ''}
                  onChange={e => patch({ documents: { ...(draft.documents || {}), source_key: e.target.value || undefined } })}>
                  <option value="">— none —</option>
                  {srcNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
          </div>
          <label style={label}>Document chooser — lookup (TABLE(DISPLAY, DATA))</label>
          <input style={input} value={draft.documents?.choose?.lookup || ''} placeholder="BOOKS_PURCHASE_ORDER(PO_NO, PO_ID)"
            onChange={e => patch({ documents: { ...(draft.documents || {}), choose: { ...(draft.documents?.choose || { lookup: '' }), lookup: e.target.value } } })} />
          <label style={label}>Chooser filter ({'{header.X}'} binds the header)</label>
          <input style={input} value={draft.documents?.choose?.filter || ''}
            placeholder="SUPPLIER_LEDGER = {header.SUPPLIER_LEDGER} AND PO_STATUS IN ('OPEN','PARTIAL')"
            onChange={e => patch({ documents: { ...(draft.documents || {}), choose: { ...(draft.documents?.choose || { lookup: '' }), filter: e.target.value } } })} />
          <label style={{ ...label, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={draft.documents?.choose?.multi !== false}
              onChange={e => patch({ documents: { ...(draft.documents || {}), choose: { ...(draft.documents?.choose || { lookup: '' }), multi: e.target.checked } } })} />
            Several documents may be pulled into one record
          </label>
          {kind !== 'business_logic' && (
            <>
              <label style={label}>Only rows where (expression over {'{src.X}'} / {'{header.X}'})</label>
              <input style={input} value={draft.where || ''} onChange={e => patch({ where: e.target.value })}
                placeholder="{src.QTY} - coalesce({src.QTY_RECEIVED}, 0) > 0" />
            </>
          )}
          <label style={label}>When the document changes and lines from the old one are present</label>
          <select style={input} value={draft.on_document_change || 'ask'}
            onChange={e => patch({ on_document_change: e.target.value as LineSource['on_document_change'] })}>
            <option value="ask">Ask the user</option>
            <option value="replace">Replace them</option>
            <option value="keep">Keep them</option>
          </select>
        </div>
      )}

      {tab === 'map' && (
        <MapTab draft={draft} patch={patch} lineCols={lineCols} srcNames={srcNames} kind={kind} />
      )}

      {tab === 'limits' && (
        <LimitsTab draft={draft} patch={patch} lineNames={lineNames} />
      )}

      {tab === 'picker' && (
        <div>
          <label style={label}>Columns the picker shows (line or source columns)</label>
          <ColumnChips value={draft.picker?.show || []} options={[...lineNames, ...srcNames.filter(n => !lineNames.includes(n))]}
            onChange={v => patch({ picker: { ...(draft.picker || {}), show: v } })} placeholder="+ column" />
          <label style={label}>Columns the user may edit in the picker</label>
          <ColumnChips value={draft.picker?.edit || []} options={lineNames}
            onChange={v => patch({ picker: { ...(draft.picker || {}), edit: v } })} placeholder="+ editable column" />
          <label style={{ ...label, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={draft.picker?.item_change !== false}
              onChange={e => patch({ picker: { ...(draft.picker || {}), item_change: e.target.checked } })} />
            Run field rules (item change) for pre-filled and edited values
          </label>
          <label style={label}>Before lines are added, check…</label>
          <select style={input} value={draft.picker?.validate || 'limits'}
            onChange={e => patch({ picker: { ...(draft.picker || {}), validate: e.target.value as 'limits' | 'object' } })}>
            <option value="limits">Only the limits</option>
            <option value="object">Each selected row with this form's validations</option>
          </select>
          <div style={help}>Saving always runs every validation; this only decides what the user sees before the lines land.</div>
          <label style={label}>Rows with errors</label>
          <select style={input} value={draft.picker?.on_invalid || 'block_add'}
            onChange={e => patch({ picker: { ...(draft.picker || {}), on_invalid: e.target.value as 'block_add' | 'skip_invalid' } })}>
            <option value="block_add">Block Add until they are fixed</option>
            <option value="skip_invalid">Add the valid rows, leave the rest in the picker</option>
          </select>
          <label style={label}>Default picker for buttons that do not choose one</label>
          <select style={input} value={draft.picker?.mode || ''}
            onChange={e => patch({ picker: { ...(draft.picker || {}), mode: (e.target.value || undefined) as 'review' | undefined } })}>
            <option value="">Automatic (review; multi for a master)</option>
            <option value="review">Pick and edit</option>
            <option value="multi">Multi-select</option>
            <option value="none">Add all, no dialog</option>
          </select>
        </div>
      )}

      {tab === 'test' && (
        <TestTab project={project} objName={model.obj_name} initialCommand={initialCommand}
          formNo={formNo} parentNames={parentNames} commandNames={commandNames} draftDirty={JSON.stringify(draft) !== JSON.stringify(source)} />
      )}
    </Modal>
  )
}

function MapTab({ draft, patch, lineCols, srcNames, kind }: {
  draft: LineSource; patch: (p: Partial<LineSource>) => void; lineCols: ColumnDef[]
  srcNames: string[]; kind: LineSourceKind
}) {
  const maps = draft.map || []
  const lineNames = lineCols.map(c => c.name)
  const hasIc = (name: string) => !!normalizeItemChange(lineCols.find(c => c.name === name)?.item_change)
  const setMap = (i: number, p: Partial<NonNullable<LineSource['map']>[number]>) =>
    patch({ map: maps.map((m, j) => (j === i ? { ...m, ...p } : m)) })
  const autoMap = () => {
    const have = new Set(maps.map(m => m.to.toLowerCase()))
    const add = lineNames.filter(n => srcNames.some(s => s.toLowerCase() === n.toLowerCase()) && !have.has(n.toLowerCase()))
      .map(n => ({ to: n, from: `{src.${srcNames.find(s => s.toLowerCase() === n.toLowerCase())}}` }))
    patch({ map: [...maps, ...add] })
  }
  const link = draft.link || { to: [] }
  return (
    <div>
      <div style={help}>
        Each mapped column takes the source's value and is PINNED: the column's own item change may read it but not
        replace it — unless "field rule may replace it" is chosen. {kind !== 'transaction' && kind !== 'master'
          ? 'A business-logic / process source already returns rows keyed by this form\'s columns, so a map is optional.' : ''}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: '0.8425rem' }}>
        <thead><tr>
          <th style={{ textAlign: 'left', padding: 4 }}>Line column</th>
          <th style={{ textAlign: 'left', padding: 4 }}>From (expression)</th>
          <th style={{ textAlign: 'left', padding: 4 }}>Who wins</th><th />
        </tr></thead>
        <tbody>
          {maps.map((m, i) => (
            <tr key={i}>
              <td style={{ padding: 3, width: '28%' }}>
                <select style={input} value={m.to} onChange={e => setMap(i, { to: e.target.value })}>
                  {lineNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </td>
              <td style={{ padding: 3 }}>
                <input style={input} value={m.from} onChange={e => setMap(i, { from: e.target.value })}
                  list={`src-cols-${i}`} />
                <datalist id={`src-cols-${i}`}>{srcNames.map(s => <option key={s} value={`{src.${s}}`} />)}</datalist>
              </td>
              <td style={{ padding: 3, width: '26%' }}>
                <select style={input} value={m.pin === false ? 'rule' : 'source'}
                  onChange={e => setMap(i, { pin: e.target.value === 'rule' ? false : undefined })}>
                  <option value="source">Source value wins</option>
                  <option value="rule">Field rule may replace it</option>
                </select>
                {hasIc(m.to) && m.pin !== false && (
                  <div style={help}>{m.to} has its own item change. Let it replace the source value?</div>
                )}
              </td>
              <td style={{ padding: 3 }}>
                <button type="button" style={smallBtn} aria-label="Remove mapping"
                  onClick={() => patch({ map: maps.filter((_, j) => j !== i) })}><Trash2 size={12} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button type="button" style={smallBtn} onClick={() => patch({ map: [...maps, { to: lineNames[0] || '', from: '' }] })}>
          <Plus size={12} /> Add mapping
        </button>
        {srcNames.length > 0 && (
          <button type="button" style={smallBtn} onClick={autoMap}><Wand2 size={12} /> Auto-map by name</button>
        )}
      </div>

      <label style={label}>Link — line columns that record where each line came from</label>
      <ColumnChips value={link.to || []} options={lineNames}
        onChange={v => patch({ link: { ...link, to: v } })} placeholder="+ link column" />
      {(kind === 'transaction' || kind === 'master') && (
        <>
          <label style={label}>…matched to these source columns (same order)</label>
          <ColumnChips value={link.from || []} options={srcNames.length ? srcNames : []}
            onChange={v => patch({ link: { ...link, from: v } })} placeholder="+ source column" />
          {srcNames.length === 0 && (
            <input style={{ ...input, marginTop: 4 }} value={(link.from || []).join(', ')} placeholder="PO_ID, LINE_NO"
              onChange={e => patch({ link: { ...link, from: e.target.value.split(',').map(x => x.trim()).filter(Boolean) } })} />
          )}
        </>
      )}
      <label style={{ ...label, display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={link.once !== false} onChange={e => patch({ link: { ...link, once: e.target.checked } })} />
        A source line may appear only once on a record
      </label>

      <label style={label}>Lock — columns the user may not change on a pulled line</label>
      <ColumnChips value={draft.lock || []} options={lineNames} onChange={v => patch({ lock: v })} placeholder="+ locked column" />
    </div>
  )
}

const LIMIT_FIELDS: Array<[keyof LineLimit, string, string]> = [
  ['min', 'Min', 'expression'], ['max', 'Max', 'expression'],
  ['tolerance', 'Tolerance', 'number'], ['tolerance_pct', 'Tol %', 'number'],
]

function LimitsTab({ draft, patch, lineNames }: {
  draft: LineSource; patch: (p: Partial<LineSource>) => void; lineNames: string[]
}) {
  const fill = draft.fill || {}
  const limits = draft.limits || {}
  const used = Array.from(new Set([...Object.keys(fill), ...Object.keys(limits)]))
  const setLimit = (col: string, p: Partial<LineLimit>) => {
    const next = { ...limits, [col]: clean({ ...(limits[col] || {}), ...p }) as LineLimit }
    if (Object.keys(next[col]).length === 0) delete next[col]
    patch({ limits: next })
  }
  const setFill = (col: string, v: string) => {
    const next = { ...fill }
    if (v) next[col] = v
    else delete next[col]
    patch({ fill: next })
  }
  const setStatus = (col: string) => {
    const next: Record<string, LineLimit> = {}
    for (const [k, v] of Object.entries(limits)) next[k] = { ...v, status: k === col ? true : undefined }
    if (!next[col]) next[col] = { status: true }
    patch({ limits: Object.fromEntries(Object.entries(next).map(([k, v]) => [k, clean(v) as LineLimit])) })
  }
  return (
    <div>
      <div style={help}>
        <b>Fill</b> is what a column STARTS at (an expression, or <code>max</code> / <code>min</code> = that column's limit).
        <b> Limits</b> are what it is ALLOWED to be — checked in the picker and again by the engine at save against the live
        source. Outside the bound but inside the tolerance: <i>warn</i> (the user may proceed) or <i>block</i>; beyond the
        tolerance is always refused. Dates compare by day (tolerance = days), date-times by minute.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: '0.8225rem' }}>
        <thead><tr>
          <th style={{ textAlign: 'left', padding: 3 }}>Column</th>
          <th style={{ textAlign: 'left', padding: 3 }}>Fill</th>
          {LIMIT_FIELDS.map(([, l]) => <th key={l} style={{ textAlign: 'left', padding: 3 }}>{l}</th>)}
          <th style={{ textAlign: 'left', padding: 3 }}>One of</th>
          <th style={{ textAlign: 'left', padding: 3 }}>Over</th>
          <th style={{ textAlign: 'left', padding: 3 }} title="This column drives the line's status chip">Status</th>
          <th />
        </tr></thead>
        <tbody>
          {used.map(col => {
            const lim = limits[col] || {}
            return (
              <tr key={col}>
                <td style={{ padding: 3, fontWeight: 600 }}>{col}</td>
                <td style={{ padding: 3 }}>
                  <input style={input} value={fill[col] || ''} onChange={e => setFill(col, e.target.value)} placeholder="max" />
                </td>
                {LIMIT_FIELDS.map(([k, , t]) => (
                  <td key={k} style={{ padding: 3, width: t === 'number' ? 70 : undefined }}>
                    <input style={input} type={t === 'number' ? 'number' : 'text'}
                      value={lim[k] == null ? '' : String(lim[k])}
                      onChange={e => setLimit(col, { [k]: t === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value } as Partial<LineLimit>)} />
                  </td>
                ))}
                <td style={{ padding: 3 }}>
                  <input style={input} value={(lim.one_of || []).join(', ')} placeholder="{src.UOM}, {src.ALT_UOM}"
                    onChange={e => setLimit(col, { one_of: e.target.value ? e.target.value.split(',').map(x => x.trim()).filter(Boolean) : undefined })} />
                </td>
                <td style={{ padding: 3, width: 80 }}>
                  <select style={input} value={lim.over || 'block'} onChange={e => setLimit(col, { over: e.target.value as 'warn' | 'block' })}>
                    <option value="block">block</option><option value="warn">warn</option>
                  </select>
                </td>
                <td style={{ padding: 3, textAlign: 'center' }}>
                  <input type="radio" name="status-col" checked={!!lim.status} onChange={() => setStatus(col)}
                    aria-label={`${col} drives the line status`} />
                </td>
                <td style={{ padding: 3 }}>
                  <button type="button" style={smallBtn} aria-label={`Remove ${col}`} onClick={() => {
                    const f = { ...fill }; delete f[col]
                    const l = { ...limits }; delete l[col]
                    patch({ fill: f, limits: l })
                  }}><Trash2 size={12} /></button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <select style={{ ...input, width: 'auto', marginTop: 6 }} value=""
        onChange={e => { if (e.target.value) setLimit(e.target.value, { over: 'block' }) }}>
        <option value="">+ fill / limit a column…</option>
        {lineNames.filter(n => !used.includes(n)).map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  )
}

function TestTab({ project, objName, formNo, parentNames, commandNames, draftDirty, initialCommand }: {
  project: string; objName: string; formNo: string; parentNames: string[]; commandNames: string[]
  draftDirty: boolean; initialCommand?: string
}) {
  const [cmd, setCmd] = useState(initialCommand && commandNames.includes(initialCommand) ? initialCommand : (commandNames[0] || ''))
  const [header, setHeader] = useState<Record<string, string>>({})
  const [docs, setDocs] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState<{ columns: Array<{ key: string; label: string; kind: string }>; rows: Array<Record<string, unknown>>; total: number; message?: string } | null>(null)
  const [applied, setApplied] = useState<Array<Record<string, unknown>> | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const payload = () => ({
    form_no: formNo, command: cmd,
    header_values: Object.fromEntries(Object.entries(header).filter(([, v]) => v !== '').map(([k, v]) => [k.toLowerCase(), v])),
    ...(docs.trim() ? { documents: docs.split(',').map(x => x.trim()).filter(Boolean) } : {}),
  })
  const run = async () => {
    setBusy(true); setErr(null); setApplied(null)
    try {
      setRes(await livePreviewExecute(project, objName, 'fetch_lines', payload()) as typeof res)
    } catch (e) {
      setErr(parseApiError(e).message); setRes(null)
    } finally { setBusy(false) }
  }
  const apply = async () => {
    if (!res) return
    setBusy(true); setErr(null)
    try {
      const out = await livePreviewExecute(project, objName, 'apply_lines', {
        ...payload(), picks: res.rows.slice(0, 5).map(r => ({ _src_key: r._src_key })),
      }) as { rows: Array<Record<string, unknown>> }
      setApplied(out.rows || [])
    } catch (e) {
      setErr(parseApiError(e).message)
    } finally { setBusy(false) }
  }
  return (
    <div>
      <div style={help}>
        A dry run of the SAVED design through the live preview: the rows the picker would offer and the lines they
        would become. Nothing is written.{draftDirty && <b> Save the source and the transaction first to test these edits.</b>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <div>
          <label style={label}>Button</label>
          <select style={input} value={cmd} onChange={e => setCmd(e.target.value)}>
            {commandNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <label style={label}>Documents (for "choose" buttons, comma-separated)</label>
          <input style={input} value={docs} onChange={e => setDocs(e.target.value)} />
        </div>
        <div>
          <label style={label}>Sample header values</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {parentNames.map(n => (
              <input key={n} style={input} placeholder={n} value={header[n] || ''}
                onChange={e => setHeader(h => ({ ...h, [n]: e.target.value }))} />
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { void run() }} disabled={busy || !cmd}>
          {busy ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={12} />} Fetch lines
        </button>
        {res && res.rows.length > 0 && (
          <button type="button" style={smallBtn} onClick={() => { void apply() }} disabled={busy}>Apply first 5</button>
        )}
      </div>
      {err && <div style={{ color: '#ef4444', fontSize: '0.8425rem', marginTop: 6 }}>{err}</div>}
      {res && (
        <div style={{ marginTop: 8, overflow: 'auto', maxHeight: 280, fontSize: '0.8125rem' }}>
          <div style={help}>{res.total} candidate row(s){res.message ? ` — ${res.message}` : ''}</div>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead><tr>{res.columns.map(c => <th key={c.kind + c.key} style={{ padding: '2px 8px', textAlign: 'left' }}>{c.label}</th>)}<th>Errors</th></tr></thead>
            <tbody>
              {res.rows.map(r => {
                const values = (r.values || {}) as Record<string, unknown>
                const disp = (r._display || {}) as Record<string, unknown>
                return (
                  <tr key={String(r._src_key)}>
                    {res.columns.map(c => <td key={c.kind + c.key} style={{ padding: '2px 8px' }}>{String((c.kind === 'line' ? values[c.key] : disp[c.key]) ?? '')}</td>)}
                    <td style={{ padding: '2px 8px', color: '#ef4444' }}>
                      {[String(r._row_error || ''), ...Object.values((r._errors || {}) as Record<string, string>)].filter(Boolean).join('; ')}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {applied && (
        <pre style={{ marginTop: 8, maxHeight: 220, overflow: 'auto', fontSize: '0.8125rem', background: 'var(--color-bg-secondary)', padding: 8, borderRadius: 4 }}>
          {JSON.stringify(applied.map(r => r.values), null, 2)}
        </pre>
      )}
    </div>
  )
}

export function emptySource(kind: LineSourceKind, name: string): LineSource {
  return { name, label: '', from: { kind }, picker: { item_change: true, validate: 'limits' } }
}

